/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-01-12 10:38:10
 */
import React, { useRef, useState, useMemo } from 'react'
import { Button, Divider, Layout, Modal, Spin } from 'antd'
import excel_icon from '../../../../../images/excel_icon.jpeg'
import FileSaver from 'file-saver'
import CONFIG from '../../../../../constants/config'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'

import { fetchLocationsByAddress } from '../../../../../actions/geoData'
import Upload from '../../../../geoData/BatchImport/Upload'
import {
  exportExcel,
  generateExcelParams
} from '../../../../geoData/BatchImport/exportExcel'
import Preview from '../../../../geoData/BatchImport/Preview'
import Amap from '../../../../geoData/BatchImport/amap'

// 定位信息接口
interface LocateInfo {
  content: string
  address: string
  lat: string
  lng: string
}
import sensors from '../../../../../utils/sensors'
import ContactUS from '../../../../components/ContactUS'
import { setEditorGeoItemList } from '../../../../../actions/watermark'
import { checkGeoItemsList, coordinateByPendingItemsList, GeoItem } from './lib'
import {
  CloseOutlined,
  CustomerServiceOutlined,
  DownloadOutlined
} from '@ant-design/icons'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

// 地理数据项接口
interface GeoDataItem {
  id: number
  content: string
  address: string
  lat: string
  lng: string
  status: number
}

interface BatchImportGeoDataProps {
  index: number
  plainItemsList: string[]
  onClose: () => void
  onFinish: (newPlainItemsList: string[]) => void
}

const BatchImportGeoData: React.FC<BatchImportGeoDataProps> = (props) => {
  const { index, plainItemsList, onClose, onFinish } = props

  const [geoItems, setGeoItems] = useState<GeoDataItem[]>([])
  const [isVisibleResults, setIsVisibleResults] = useState(false)
  const [isVisibleMap, setIsVisibleMap] = useState(false)
  const [editRecord, setEditRecord] = useState<GeoDataItem | null>(null)
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const filenameRef = useRef('')
  const successGeoItems = useMemo(
    () => geoItems.filter((record) => record.status === 1),
    [geoItems]
  )
  const geoItemsList = useAppSelector(
    (state) => state.editor.geoItemsList
  ) as GeoItem[] //记录watermark.itemsList
  const watermark = useAppSelector((state) => state.editor.watermark) //watermark.content

  /**
   * @param locations 地址信息列表
   */
  const getLocationsByAddress = (
    locations: Array<{
      content: string
      address: string
      lat: string
      lng: string
    }>
  ) => {
    setLoading(true)
    dispatch(
      fetchLocationsByAddress({
        records: locations
      })
    ).then((data: any) => {
      if (data.status === 0) {
        const items: GeoDataItem[] = data.records.map(
          (item: any, idx: number) => ({
            ...item,
            id: idx + 1
          })
        )
        setGeoItems(items)
        setIsVisibleResults(true)
        setLoading(false)
      }
    })
  }

  const handleImportClick = () => {
    reportSaClickEvent('confirm')

    const { newGeoItemsList, newPlainItemsList } = coordinateByPendingItemsList(
      watermark?.id || '',
      watermark?.items?.[index]?.id || '',
      plainItemsList,
      geoItemsList,
      geoItems.filter((item) => item.status === 1)
    )
    const { hasOver8Locations, reducedGeoItemsList } =
      checkGeoItemsList(newGeoItemsList)
    if (hasOver8Locations) {
      Modal.info({
        title: '同一个选项最多可绑定8个地点',
        content: '保存后将删除最早上传的地点',
        zIndex: 10000,
        onOk: () => {
          dispatch(setEditorGeoItemList(reducedGeoItemsList))
          onFinish(newPlainItemsList)
          onClose()
        }
      })
    } else {
      dispatch(setEditorGeoItemList(newGeoItemsList))
      onFinish(newPlainItemsList)
      onClose()
    }
  }

  //下载定位失败列表
  const handleLocationsErrorClick = () => {
    reportSaClickEvent('downloadErrorItem')

    exportExcel(
      generateExcelParams,
      geoItems.filter((item) => item.status !== 1) as any
    )
  }

  //调整坐标
  const adjustPosition = (
    location: { longitude: number; latitude: number },
    address: string
  ) => {
    reportSaClickEvent('adjustPosition')
    const { longitude, latitude } = location
    const newRecord: GeoDataItem = {
      ...editRecord!,
      lng: String(longitude),
      lat: String(latitude),
      address: address,
      status: 1
    }

    const newGeoItems = geoItems.map((item) => {
      if (item.id === newRecord.id) {
        return newRecord
      }
      return item
    })

    setGeoItems(newGeoItems)
    setEditRecord(null)
    setIsVisibleMap(false)
  }

  const handleCloseClick = () => {
    reportSaClickEvent('close')
    onClose()
  }

  const reportSaClickEvent = (clickItem: string) => {
    sensors.track('web_click_page_watermark_item_add_multi_upload', {
      clickItem,
      fileName: filenameRef.current,
      token: '',
      pageType: 'groupWatermarkPage',
      fromPlace: 'watermarkOption'
    })
  }

  const reportSaUploadEvent = (content: string) => {
    sensors.track('web_get_action_watermark_item_add_multi_upload', {
      content,
      fileName: filenameRef.current,
      token: '',
      fromPlace: 'watermarkOption'
    })
  }

  return (
    <Layout className="watermark-geo-data">
      <PageHeader
        className="Header"
        title="批量添加选项"
        extra={
          <div className="header-extra">
            <ContactUS fromPage="水印管理-批量添加选项">
              <span
                className="service"
                onClick={() => reportSaClickEvent('contactUs')}
              >
                <CustomerServiceOutlined />
                联系客服
              </span>
            </ContactUS>

            <CloseOutlined className="close" onClick={handleCloseClick} />
          </div>
        }
      />
      <Content className="content geo-data-content">
        <div className="batch-import-container">
          <div className="operate-wrapper">
            <div className="step1">
              <div className="title">1.下载模板，填写水印选项</div>
              <img src={excel_icon} className="excel-icon" alt="" />
              <div>《水印选项模板》</div>
              <Button
                icon={<DownloadOutlined />}
                className="download-template"
                onClick={() => {
                  FileSaver.saveAs(CONFIG.geoDataExcelUrl, '水印选项模板.xlsx')
                  reportSaClickEvent('download')
                }}
              >
                立即下载
              </Button>
            </div>
            <div className="step2">
              <div className="title">2.上传填写完毕的Excel文件</div>
              <Upload
                getLocationsByAddress={getLocationsByAddress}
                reportSaClickEvent={reportSaClickEvent}
                reportSaUploadEvent={reportSaUploadEvent}
                afterUpload={(filename: string) =>
                  (filenameRef.current = filename)
                }
              />
            </div>
          </div>

          {isVisibleResults && (
            <>
              <Divider className="divider" />
              <Preview
                onAdjust={(record: any) => {
                  setIsVisibleMap(true)
                  setEditRecord(record)
                }}
                geoItems={geoItems as any}
                onImportClick={handleImportClick}
                onLocationsErrorClick={handleLocationsErrorClick}
              />
            </>
          )}
        </div>

        <Modal
          open={loading}
          footer={null}
          closable={false}
          getContainer={false}
          width={304}
        >
          <div className="loading-wrapper">
            <Spin size="large" />
            <div className="loading-text">上传成功，正在解析...</div>
          </div>
        </Modal>

        {isVisibleMap && editRecord && (
          <Amap
            handleOk={(location, address) => {
              adjustPosition(location, address)
            }}
            handleCancel={() => {
              setIsVisibleMap(false)
            }}
            locateInfo={editRecord as LocateInfo}
          />
        )}
      </Content>
    </Layout>
  )
}

export default BatchImportGeoData
